import { useEffect, useRef, useState } from 'react';
import { Icono } from '../componentes/Icono';
import { T_PUERTA } from './textosPuerta';

/**
 * La cámara de la puerta (6.1): lee QR con el lector del navegador (`BarcodeDetector`, en Safari del iPad y en Chrome de
 * Android). Si el navegador no lo tiene o no da permiso, lo dice en palabras y el lector de pistola y la búsqueda siguen.
 * Una misma lectura no se repite en 3 segundos.
 */
interface Detector {
    detect: (fuente: HTMLVideoElement) => Promise<{ rawValue: string }[]>;
}
type ConstructorDetector = new (o: { formats: string[] }) => Detector;

const ERRORES: Readonly<Record<string, string>> = {
    NotAllowedError: 'No tenemos permiso para usar la cámara',
    NotFoundError: 'Esta tablet no tiene cámara o no la encontramos',
    NotReadableError: 'Otra app está usando la cámara',
    SecurityError: 'La cámara solo funciona en una dirección segura',
    OverconstrainedError: 'La cámara elegida ya no está',
};

export function Camara({ abierta, onLeido }: { abierta: boolean; onLeido: (valor: string) => void }) {
    const video = useRef<HTMLVideoElement | null>(null);
    const [error, setError] = useState<string | null>(null);
    const ultimo = useRef<{ valor: string; cuando: number } | null>(null);
    const leido = useRef(onLeido);
    useEffect(() => {
        leido.current = onLeido;
    }, [onLeido]);
    const Lector = (globalThis as { BarcodeDetector?: ConstructorDetector }).BarcodeDetector;
    const sinLector = Lector === undefined;

    useEffect(() => {
        if (!abierta || Lector === undefined || typeof navigator === 'undefined') {
            return;
        }
        let flujo: MediaStream | null = null;
        let reloj: ReturnType<typeof setInterval> | null = null;
        let vivo = true;
        const detector = new Lector({ formats: ['qr_code'] });
        navigator.mediaDevices
            .getUserMedia({ video: { facingMode: 'environment' } })
            .then((f) => {
                if (!vivo) {
                    f.getTracks().forEach((t) => { t.stop(); });
                    return;
                }
                flujo = f;
                const v = video.current;
                if (v !== null) {
                    v.srcObject = f;
                    void v.play().catch(() => undefined);
                }
                reloj = setInterval(() => {
                    const fuente = video.current;
                    if (fuente === null || fuente.readyState < 2) {
                        return;
                    }
                    detector
                        .detect(fuente)
                        .then((codigos) => {
                            const valor = codigos[0]?.rawValue;
                            if (valor === undefined || valor === '') {
                                return;
                            }
                            const ahora = Date.now();
                            if (ultimo.current?.valor === valor && ahora - ultimo.current.cuando < 3000) {
                                return;
                            }
                            ultimo.current = { valor, cuando: ahora };
                            leido.current(valor);
                        })
                        .catch(() => undefined);
                }, 300);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(ERRORES[(e as { name?: string }).name ?? ''] ?? 'No hemos podido abrir la cámara');
                }
            });
        return () => {
            vivo = false;
            if (reloj !== null) {
                clearInterval(reloj);
            }
            flujo?.getTracks().forEach((t) => { t.stop(); });
        };
    }, [abierta, Lector]);

    if (!abierta) {
        return (
            <div className="rv-pt-camara rv-pt-camara--cerrada">
                <Icono nombre="camera-off" />
                <strong>{T_PUERTA.camaraCerrada}</strong>
                <span>{T_PUERTA.camaraCerradaTexto}</span>
            </div>
        );
    }
    if (sinLector || error !== null) {
        return (
            <div className="rv-pt-camara rv-pt-camara--cerrada" role="status">
                <Icono nombre="camera-off" />
                <strong>{error ?? T_PUERTA.sinLectorCamara}</strong>
                {error !== null && <span>El lector de pistola y la búsqueda siguen funcionando.</span>}
            </div>
        );
    }
    return (
        <div className="rv-pt-camara">
            <video ref={video} muted playsInline aria-label={T_PUERTA.apunta} />
            <span className="rv-pt-camara__ayuda"><Icono nombre="scan-line" />{T_PUERTA.apunta}</span>
        </div>
    );
}
